/**
 * WorkdayParts.js
 *
 * Small shared building blocks of the Munkanap surfaces: orange gradient primary button, outlined secondary button,
 * stat tile, the 15 / 30 / 60 minute snooze segmented control and the office / home-office selector.
 * Theme-aware (light + dark), no native dialogs.
 *
 * @module components/workday/WorkdayParts
 */

import React from 'react';
import {ActivityIndicator, Pressable, StyleSheet, Text, View} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import {useTranslation} from 'react-i18next';
import Icon from '../management/SafeIcon';
import {BRAND, useManagementTheme, withAlpha} from '../../helpers/managementTheme';
import {useWorkday} from '../../helpers/WorkdayContext';
import useWorkdayStopFlow from './useWorkdayStopFlow';
import haptics from '../../helpers/haptics';

const MODE_OPTIONS = [
    {mode: 'OFFICE', icon: 'office-building-outline', labelKey: 'workday_mode_office'},
    {mode: 'HOME_OFFICE', icon: 'home-outline', labelKey: 'workday_mode_home'},
];

/**
 * Orange gradient primary button.
 * @param {{label: string, icon?: string, onPress: () => void, busy?: boolean, disabled?: boolean, tone?: 'brand'|'danger'}} props props.
 * @returns {JSX.Element} button.
 */
export function PrimaryButton({label, icon, onPress, busy = false, disabled = false, tone = 'brand'}) {
    const colors = tone === 'danger' ? ['#EF4444', '#DC2626'] : [BRAND.base, BRAND.light];

    return (
        <Pressable
            onPress={onPress}
            disabled={disabled || busy}
            accessibilityRole="button"
            accessibilityLabel={label}
            style={({pressed}) => [styles.primaryWrap, (disabled || busy) && styles.disabled, pressed && styles.pressed]}
        >
            <LinearGradient colors={colors} start={{x: 0, y: 0}} end={{x: 1, y: 1}} style={styles.primary}>
                {busy ? <ActivityIndicator color="#FFFFFF" /> : (
                    <>
                        {!!icon && <Icon name={icon} size={20} color="#FFFFFF" />}
                        <Text style={styles.primaryText} numberOfLines={1}>{label}</Text>
                    </>
                )}
            </LinearGradient>
        </Pressable>
    );
}

/**
 * Outlined secondary button.
 * @param {{label: string, icon?: string, onPress: () => void, tone?: string, flex?: boolean}} props props.
 * @returns {JSX.Element} button.
 */
export function OutlineButton({label, icon, onPress, tone, flex = false}) {
    const m = useManagementTheme();

    return (
        <Pressable
            onPress={onPress}
            accessibilityRole="button"
            accessibilityLabel={label}
            style={({pressed}) => [styles.outline, flex && {flex: 1}, {backgroundColor: m.colors.surface, borderColor: m.colors.border}, pressed && styles.pressed]}
        >
            {!!icon && <Icon name={icon} size={18} color={tone || m.colors.primary} />}
            <Text style={[styles.outlineText, {color: m.colors.heading}]} numberOfLines={2}>{label}</Text>
        </Pressable>
    );
}

/**
 * Soft elevated stat tile.
 * @param {{icon: string, label: string, value: string, accent?: string}} props props.
 * @returns {JSX.Element} tile.
 */
export function StatTile({icon, label, value, accent}) {
    const m = useManagementTheme();
    const color = accent || BRAND.base;

    return (
        <View style={[styles.tile, {backgroundColor: m.colors.surfaceAlt, borderColor: m.colors.borderSoft}]}>
            <View style={[styles.tileIcon, {backgroundColor: withAlpha(color, m.isDark ? 0.24 : 0.14)}]}>
                <Icon name={icon} size={16} color={color} />
            </View>
            <Text style={[styles.tileValue, {color: m.colors.heading}]} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.75}>{value}</Text>
            <Text style={[styles.tileLabel, {color: m.colors.subText}]} numberOfLines={1}>{label}</Text>
        </View>
    );
}

/**
 * 15 / 30 / 60 minute snooze segmented control.
 * @param {{options: number[], onPick: (minutes: number) => void, disabled?: boolean}} props props.
 * @returns {JSX.Element} control.
 */
export function SnoozeSegments({options, onPick, disabled = false}) {
    const {t} = useTranslation();
    const m = useManagementTheme();

    return (
        <View style={[styles.segments, {backgroundColor: m.colors.surfaceAlt, borderColor: m.colors.borderSoft}]}>
            {options.map((minutes) => (
                <Pressable
                    key={minutes}
                    disabled={disabled}
                    onPress={() => onPick(minutes)}
                    accessibilityRole="button"
                    accessibilityLabel={`${t('workday_snooze_title')}: ${t('workday_snooze_minutes', {count: minutes})}`}
                    style={({pressed}) => [styles.segment, pressed && styles.pressed, disabled && styles.disabled]}
                >
                    <Icon name="bell-sleep-outline" size={15} color={BRAND.base} />
                    <Text style={[styles.segmentText, {color: m.colors.heading}]} numberOfLines={1}>{t('workday_snooze_minutes', {count: minutes})}</Text>
                </Pressable>
            ))}
        </View>
    );
}

/**
 * Office / home-office selector ("Munkavégzés helye").
 * - Not running + GPS enabled for the store: an info row only (the clock-in fix decides).
 * - Not running, no GPS: Iroda / Home office segments bound to the remembered choice (sent on start).
 * - Running, decided by GPS: a locked chip ("Iroda · GPS alapján · <place>").
 * - Running, manual / default: the segments switch the running day.
 * @param {{tone?: 'light'|'dark'}} [props] `dark` = drawn on the navy hero gradient.
 * @returns {JSX.Element|null} selector, or null while the feature is unavailable.
 */
export function WorkModeSelector({tone = 'light'}) {
    const {t} = useTranslation();
    const m = useManagementTheme();
    const {supported, loaded, state, busy, gpsWorkMode, workModePref, setWorkModePref, setWorkMode} = useWorkday();

    if (!supported || !loaded || !state) {
        return null;
    }

    const running = state.running;
    const dark = tone === 'dark';
    const textColor = dark ? '#FFFFFF' : m.colors.heading;
    const subColor = dark ? 'rgba(226,236,255,0.78)' : m.colors.subText;
    const boxStyle = dark
        ? {backgroundColor: 'rgba(255,255,255,0.10)', borderColor: 'rgba(255,255,255,0.18)'}
        : {backgroundColor: m.colors.surfaceAlt, borderColor: m.colors.borderSoft};
    const gpsLocked = !!running && running.workModeSource === 'gps';

    if (gpsLocked || (!running && gpsWorkMode)) {
        const option = MODE_OPTIONS.find((o) => o.mode === running?.workMode);
        const detail = running
            ? [t(option?.labelKey || 'workday_mode_office'), t('workday_mode_by_gps'), running.workModePlaceName].filter(Boolean).join(' · ')
            : t('workday_mode_auto_hint');

        return (
            <View style={[styles.modeInfo, boxStyle]} accessibilityRole="text" accessibilityLabel={`${t('workday_mode_title')}: ${detail}`}>
                <Icon name={running ? (option?.icon || 'office-building-outline') : 'crosshairs-gps'} size={17} color={BRAND.base} />
                <Text style={[styles.modeInfoText, {color: running ? textColor : subColor}]} numberOfLines={2}>{detail}</Text>
                {running && <Icon name="crosshairs-gps" size={14} color={subColor} />}
            </View>
        );
    }

    const current = running ? running.workMode : workModePref;
    const onPick = (mode) => (running ? setWorkMode(mode) : setWorkModePref(mode));

    return (
        <View style={styles.modeBlock}>
            <Text style={[styles.modeTitle, {color: subColor}]} numberOfLines={1}>{t('workday_mode_title')}</Text>
            <View style={[styles.segments, boxStyle]} accessibilityRole="radiogroup">
                {MODE_OPTIONS.map((o) => {
                    const active = current === o.mode;

                    return (
                        <Pressable
                            key={o.mode}
                            disabled={busy}
                            onPress={() => onPick(o.mode)}
                            accessibilityRole="radio"
                            accessibilityState={{selected: active, disabled: busy}}
                            accessibilityLabel={t(o.labelKey)}
                            style={({pressed}) => [
                                styles.segment,
                                active && {backgroundColor: withAlpha(BRAND.base, dark ? 0.32 : (m.isDark ? 0.26 : 0.14))},
                                pressed && styles.pressed,
                                busy && styles.disabled,
                            ]}
                        >
                            <Icon name={o.icon} size={16} color={active ? BRAND.base : subColor} />
                            <Text style={[styles.segmentText, {color: active ? textColor : subColor}]} numberOfLines={1}>{t(o.labelKey)}</Text>
                        </Pressable>
                    );
                })}
            </View>
        </View>
    );
}

/**
 * The ONE control row of a running workday: Pause / Resume (brand-orange primary) next to Stop (red outline, with the
 * HappyAlert confirmation). Shared by the status sheet, the briefing hero and the Time Tracker so the verbs, haptics and
 * confirmation never drift between surfaces. Stop stays available while paused.
 * @param {{onStop?: () => void, compact?: boolean}} props `onStop` overrides the default confirm flow (the sheet closes
 *   itself before the confirmation opens); `compact` drops the labels' icons for tight strips.
 * @returns {JSX.Element|null} control row, or null when no workday runs.
 */
export function WorkdayControls({onStop, compact = false}) {
    const {t} = useTranslation();
    const {state, busy, paused, pause, resume} = useWorkday();
    const {requestStop} = useWorkdayStopFlow();

    if (!state?.running) {
        return null;
    }

    const onToggle = () => {
        haptics.medium();
        (paused ? resume : pause)();
    };
    const onEnd = () => {
        haptics.medium();
        (onStop || requestStop)();
    };

    return (
        <View style={styles.controls}>
            <View style={styles.controlMain}>
                <PrimaryButton
                    label={paused ? t('workday_resume') : t('workday_pause')}
                    icon={compact ? undefined : (paused ? 'play-circle-outline' : 'pause-circle-outline')}
                    onPress={onToggle}
                    disabled={busy}
                />
            </View>
            <Pressable
                onPress={onEnd}
                disabled={busy}
                accessibilityRole="button"
                accessibilityLabel={t('workday_stop')}
                style={({pressed}) => [styles.endBtn, busy && styles.disabled, pressed && styles.pressed]}
            >
                <Icon name="stop-circle-outline" size={20} color="#DC2626" />
                <Text style={styles.endText} numberOfLines={1}>{t('workday_stop_short')}</Text>
            </Pressable>
        </View>
    );
}

const styles = StyleSheet.create({
    controls: {flexDirection: 'row', gap: 10, alignItems: 'stretch'},
    controlMain: {flex: 1.25},
    endBtn: {flex: 1, minHeight: 52, borderRadius: 16, borderWidth: 1.4, borderColor: '#DC2626', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingHorizontal: 8},
    endText: {color: '#DC2626', fontSize: 14, fontWeight: '900'},
    modeBlock: {gap: 6},
    modeTitle: {fontSize: 11.5, fontWeight: '800', letterSpacing: 0.4},
    modeInfo: {flexDirection: 'row', alignItems: 'center', gap: 8, borderRadius: 16, borderWidth: 1, paddingHorizontal: 12, paddingVertical: 10},
    modeInfoText: {flex: 1, fontSize: 13, fontWeight: '700'},
    primaryWrap: {borderRadius: 18, overflow: 'hidden'},
    primary: {minHeight: 54, borderRadius: 18, paddingHorizontal: 20, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10},
    primaryText: {color: '#FFFFFF', fontSize: 16, fontWeight: '900', letterSpacing: 0.1},
    outline: {minHeight: 50, borderRadius: 16, borderWidth: 1, paddingHorizontal: 12, paddingVertical: 8, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8},
    outlineText: {fontSize: 13.5, fontWeight: '800', textAlign: 'center', flexShrink: 1},
    tile: {flex: 1, borderRadius: 18, borderWidth: 1, paddingVertical: 12, paddingHorizontal: 10, alignItems: 'center', gap: 4},
    tileIcon: {width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center'},
    tileValue: {fontSize: 18, fontWeight: '900', fontVariant: ['tabular-nums']},
    tileLabel: {fontSize: 11.5, fontWeight: '700'},
    segments: {flexDirection: 'row', borderRadius: 18, borderWidth: 1, padding: 4, gap: 4},
    segment: {flex: 1, minHeight: 44, borderRadius: 14, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6},
    segmentText: {fontSize: 13.5, fontWeight: '800'},
    disabled: {opacity: 0.5},
    pressed: {opacity: 0.75, transform: [{scale: 0.98}]},
});
